import React from 'react';
import classNames from 'classnames/bind';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Skeleton } from '@mantine/core';

import { ApplicationParams } from 'types/route_params';
import { Deployment, DeploymentConfiguration } from 'types/deployment';

import { ActivePipelineContext } from 'pipelines/context/active_pipeline';
import { useToggle } from '@mantine/hooks';

import APIError from 'types/api_error';
import Pipeline from 'types/pipeline';
import { Gateway } from 'types/gateway';

import { isNotNullOrUndefined } from 'services/nullable';
import { pluralize } from 'services/string';
import { useGateway } from 'gateways/hooks/useGateway';
import { useUpdateDeployment } from 'deployments/hooks/useUpdateDeployment';

import { Accordion } from 'components/Accordion/Accordion';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { SkeletonList } from 'components/Skeleton/SkeletonList';

import { DeploymentFormActions } from '../Actions';
import { DeploymentFormContent, DeploymentFormContentRef } from '../Content';
import { DeploymentFormErrorMessage } from '../DeploymentFormErrorMessage';
import { DeploymentFormProvider } from '../context/deployment_form_provider';
import { DeploymentFormSkeleton } from '../Skeleton';
import { useDeploymentFormContent } from '../useDeploymentFormContent';
import { usePreloadDeploymentForm } from '../usePreloadDeploymentForm';
import styles from '../Form.module.scss';

const c = classNames.bind(styles);

export type UpdateDeploymentsFormStep2Props = {
  pipeline: Pipeline;
  deployments?: Deployment[];
  count: number;
  isLoading: boolean;
  isError: boolean;
  onSuccess?: (deployments: Deployment[]) => void;
};

export function UpdateDeploymentsFormStep2({
  pipeline,
  deployments,
  count,
  isLoading,
  isError,
  onSuccess,
}: UpdateDeploymentsFormStep2Props) {
  const { applicationID } = useParams<ApplicationParams>();

  const { isLoading: isLoadingFormComponents, error } =
    usePreloadDeploymentForm();

  const [errors, setErrors] = React.useState<
    Record<Deployment['id'], APIError | undefined>
  >({});

  const [shouldStartImmediately, toggleShouldStartImmediately] = useToggle([
    true,
    false,
  ]);

  const formContentRefs = React.useRef<Array<DeploymentFormContentRef | null>>(
    []
  );

  const { mutateAsync: updateDeployment, isLoading: isUpdating } =
    useUpdateDeployment({
      onSuccess() {
        if (!deployments) {
          return;
        }

        onSuccess?.(deployments);
      },
    });

  async function submitDeployments() {
    if (!formContentRefs.current) {
      return;
    }

    const formSubmissions = formContentRefs.current
      .map((form) => form?.onSubmit())
      .filter(isNotNullOrUndefined);

    try {
      const configurations = await Promise.all(formSubmissions);

      configurations.forEach(({ deployment, configuration, gateway }) => {
        if (!deployment || !gateway) {
          return;
        }

        redeploy(deployment, gateway, configuration);
      });
    } catch {
      // Do nothing. Errors are displayed inline
    }
  }

  function redeploy(
    deployment: Deployment,
    gateway: Gateway,
    configuration: DeploymentConfiguration = {}
  ) {
    if (!pipeline || !applicationID || !gateway) {
      return;
    }

    const { id } = deployment;
    let state = shouldStartImmediately
      ? ('running' as const)
      : ('stopped' as const);

    if (!gateway) {
      return;
    }

    if (gateway.status === 'offline') {
      state = 'stopped';
    }

    updateDeployment({ id, configuration, state }).catch((error) =>
      // Form errors
      setErrors((previous) => ({ ...previous, [id]: error }))
    );
  }

  if (!pipeline) {
    return null;
  }

  if (isError) {
    return (
      <FormMessage intent="danger" icon="warning">
        <strong>{error?.message || 'An unknown error occurred.'}</strong>
        <br />
        Please try again later.
      </FormMessage>
    );
  }

  if (isLoading || isLoadingFormComponents || !deployments) {
    if (count === 1) {
      return <DeploymentFormSkeleton />;
    }

    return (
      <SkeletonList
        min={count}
        max={count}
        component={<Accordion label={<Skeleton w="50%" />} disabled />}
      />
    );
  }

  return (
    <DeploymentFormProvider pipeline={pipeline}>
      <div className={c('container')}>
        <section className={c('body')}>
          <ActivePipelineContext.Provider value={pipeline}>
            {deployments.map((deployment, index) => (
              <ForwardInnerRef
                deployment={deployment}
                error={errors[deployment.id]}
                isSingleDeployment={deployments.length === 1}
                key={deployment.id}
                ref={(el: any) => (formContentRefs.current[index] = el)}
              />
            ))}
          </ActivePipelineContext.Provider>
        </section>
      </div>

      <DeploymentFormActions
        pipeline={pipeline}
        onSubmit={submitDeployments}
        isDeploying={isUpdating}
        startImmediately={shouldStartImmediately}
        onToggleStartImmediately={toggleShouldStartImmediately}
        deployments={deployments}
        buttonText={`Update ${deployments.length} ${pluralize(deployments.length, 'deployment')}`}
        isUpdateDeploymentsForm
      />
    </DeploymentFormProvider>
  );
}

type InnerProps = {
  deployment: Deployment;
  error?: APIError;
  isSingleDeployment?: boolean;
};

function Inner(
  { deployment, error, isSingleDeployment }: InnerProps,
  ref: React.ForwardedRef<DeploymentFormContentRef>
) {
  const { data: gateway, isLoading } = useGateway(deployment.gateway_id);

  const formMethods = useDeploymentFormContent(deployment);

  if (isLoading) {
    return (
      <Accordion
        label={<Skeleton w="50%">{deployment.name}</Skeleton>}
        disabled
      />
    );
  }

  const hasError = Object.keys(formMethods.formState.errors).length > 0;

  const form = (
    <section
      className={c('field', 'deployment')}
      aria-label={`Configure parameters for deployment ${deployment.name}`}
    >
      {error && <DeploymentFormErrorMessage error={error} />}

      <FormProvider {...formMethods}>
        <DeploymentFormContent
          deployment={deployment}
          gateway={gateway}
          ref={ref}
        />
      </FormProvider>
    </section>
  );

  if (isSingleDeployment) {
    return form;
  }

  return (
    <Accordion
      label={deployment.name || deployment.id}
      className={c('form-accordion', { error: hasError })}
    >
      {form}
    </Accordion>
  );
}

const ForwardInnerRef = React.forwardRef<DeploymentFormContentRef, InnerProps>(
  Inner
);
